import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import { trackGA4PreNav } from '@gumtree/shared/src/util/ga4-shared';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { Icon } from '@gumtree/ui-library';
import { POLLING_INTERVAL_MS } from '@gumtree/shared/src/constants/message-centre';
import { getDomainFromUrl } from '@gumtree/shared/src/util/url-service';
import type { ShellState } from '../../../reducers/common';
import { MessagesButton, StyledCountBullet, StyledWrapper } from './style';
import { MhhNavIcon } from '../style';
import { fetchUnreadMessagesCount } from '../../../reducers/common/unread-messages';
import { ShellUseDispatch } from '../../../reducers/common';
import logger from '../../../logger';

interface Props {
    isMhhExperiment?: boolean;
    isMhcDesktopExperiment?: boolean;
}

export default function NavBarMessagesButton(props: Props) {
    const dispatch = useDispatch() as ShellUseDispatch;
    const {
        baseConfig: { bffUrl, pageType },
        unreadMessagesCount,
    } = useSelector(selectState);

    const showUnreadCount = useFeatureIsOn(GrowthBookFeature.UNREAD_FEATURE_FLAG);

    useEffect(() => {
        if (showUnreadCount === false || pageType === 'MyMessages') {
            return;
        }

        let intervalId = 0;
        async function intervalCallback() {
            try {
                const shouldPoll = await dispatch(fetchUnreadMessagesCount());
                if (!shouldPoll) {
                    clearInterval(intervalId);
                }
            } catch (error: any) {
                logger.error('Error fetching unread messages count:', error);
            }
        }
        intervalId = window.setInterval(intervalCallback, POLLING_INTERVAL_MS);
        intervalCallback();

        return () => clearInterval(intervalId);
    }, [pageType, showUnreadCount]);

    const unreadMessagesCountToDisplay = Math.min(unreadMessagesCount, 99);

    const handleNavigation = useCallback(() => {
        const targetUrl = `${bffUrl}/manage/messages`;

        requestAnimationFrame(() => {
            setTimeout(() => {
                trackV2('manageMessagesHeader');
                trackGA4PreNav<GA4.ClickPrimaryNavigation>({
                    event: 'click_primary_navigation',
                    linkDomain: getDomainFromUrl(targetUrl),
                    linkText: 'Messages',
                    linkURL: targetUrl,
                    navigationCategory: 'menu',
                    navigationHeader: undefined,
                    navigationHierarchy: 'header',
                    pageClickedFrom: pageType,
                });
                window.location.href = targetUrl;
            }, 0);
        });
    }, [bffUrl, pageType]);

    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            handleNavigation();
        },
        [handleNavigation]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLAnchorElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleNavigation();
            }
        },
        [handleNavigation]
    );

    return (
        <StyledWrapper>
            <MessagesButton
                {...qaAttribute('my-gumtree-manage-messages')}
                data-testid="my-gumtree-manage-messages"
                aria-label="Messages"
                className="messages-button"
                dark
                label="Messages"
                icon={
                    props.isMhhExperiment ? (
                        <MhhNavIcon type="navbar-messages" />
                    ) : (
                        <Icon type="envelope" />
                    )
                }
                href={`${bffUrl}/manage/messages`}
                bottomLabel
                mhhStyle={props.isMhhExperiment}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
            />
            {showUnreadCount && Boolean(unreadMessagesCount) && (
                <StyledCountBullet
                    mhhStyle={props.isMhhExperiment}
                    mhcDesktopStyle={props.isMhcDesktopExperiment}
                >
                    {unreadMessagesCountToDisplay}
                </StyledCountBullet>
            )}
        </StyledWrapper>
    );
}

function selectState({ baseConfig, unreadMessagesCount }: ShellState) {
    return {
        baseConfig,
        unreadMessagesCount,
    };
}
